/**
 * Scout BrandVoiceSettings Component
 * Settings panel for configuring AI outreach voice and style
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  Save, 
  Loader2, 
  Check,
  MessageSquare,
  Building2,
  FileText,
  Sparkles,
  X,
} from 'lucide-react';

// API base URL
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const TONE_OPTIONS = [
  { id: 'conversational', label: 'Conversational', desc: 'Friendly and approachable' },
  { id: 'professional', label: 'Professional', desc: 'Formal and business-focused' },
  { id: 'technical', label: 'Technical', desc: 'Industry-specific and detailed' },
  { id: 'casual', label: 'Casual', desc: 'Relaxed and personable' },
  { id: 'authoritative', label: 'Authoritative', desc: 'Expert and confident' },
];

export default function BrandVoiceSettings({ isOpen, onClose }) {
  const { getAccessTokenSilently } = useAuth0();
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  
  // Brand voice fields
  const [name, setName] = useState('');
  const [tone, setTone] = useState('professional');
  const [companyName, setCompanyName] = useState('');
  const [signature, setSignature] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');
  const [valueProps, setValueProps] = useState(['']);
  const [sampleMessages, setSampleMessages] = useState(['']);

  // Source tracking: 'personal', 'org', or 'none'
  const [voiceSource, setVoiceSource] = useState('none');
  const [hasOrgVoice, setHasOrgVoice] = useState(false);

  // Fetch existing brand voice
  const fetchBrandVoice = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE}/api/ai/brand-voice`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      
      if (data.success) {
        setVoiceSource(data.source || 'none');
        setHasOrgVoice(!!(data.orgBrandVoice && Object.keys(data.orgBrandVoice).length > 0));

        const bv = data.brandVoice || {};
        setName(bv.name || '');
        setTone(bv.tone || 'professional');
        setCompanyName(bv.company_name || '');
        setSignature(bv.signature || '');
        setCustomInstructions(bv.custom_instructions || '');
        setValueProps(bv.value_props?.length ? bv.value_props : ['']);
        setSampleMessages(bv.sample_messages?.length ? bv.sample_messages : ['']);
      }
    } catch (err) {
      console.error('Failed to fetch brand voice:', err);
      setError('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  }, [getAccessTokenSilently]);

  const handleSwitchToOrg = async () => {
    setIsSaving(true);
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API_BASE}/api/ai/brand-voice`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ brandVoice: {} }),
      });
      await fetchBrandVoice();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError('Failed to switch to team voice');
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchBrandVoice();
    }
  }, [isOpen, fetchBrandVoice]);

  // Save brand voice
  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSaved(false);

    try {
      const token = await getAccessTokenSilently();
      const brandVoice = {
        name: name.trim(),
        tone,
        company_name: companyName.trim(),
        signature: signature.trim(),
        custom_instructions: customInstructions.trim(),
        value_props: valueProps.filter(v => v.trim()),
        sample_messages: sampleMessages.filter(m => m.trim()),
      };

      const response = await fetch(`${API_BASE}/api/ai/brand-voice`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ brandVoice }),
      });

      const data = await response.json();
      
      if (data.success) {
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
      } else {
        throw new Error(data.error || 'Failed to save');
      }
    } catch (err) {
      console.error('Failed to save brand voice:', err);
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle value prop changes
  const handleValuePropChange = (index, value) => {
    const newProps = [...valueProps];
    newProps[index] = value;
    setValueProps(newProps);
  };

  const addValueProp = () => {
    setValueProps([...valueProps, '']);
  };

  const removeValueProp = (index) => {
    if (valueProps.length > 1) {
      setValueProps(valueProps.filter((_, i) => i !== index));
    }
  };

  // Handle sample message changes
  const handleSampleChange = (index, value) => {
    const newSamples = [...sampleMessages];
    newSamples[index] = value;
    setSampleMessages(newSamples);
  };

  const addSampleMessage = () => {
    setSampleMessages([...sampleMessages, '']);
  };

  const removeSampleMessage = (index) => {
    if (sampleMessages.length > 1) {
      setSampleMessages(sampleMessages.filter((_, i) => i !== index));
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] rounded-lg">
              <Sparkles size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold font-['Playfair_Display']">
                Brand Voice
              </h2>
              <p className="text-sm text-gray-500">
                {voiceSource === 'personal' ? 'Using your personal brand voice' :
                 voiceSource === 'org' ? "Using your team's brand voice" :
                 'No brand voice set — configure one to personalise outreach'}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 size={24} className="animate-spin text-[#5a20ff]" />
            </div>
          ) : (
            <>
              {/* Voice Name */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <MessageSquare size={14} />
                  Voice Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Shelly's conversational tone"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#5a20ff]"
                />
                <p className="mt-1 text-xs text-gray-400">
                  A friendly name to identify this voice style
                </p>
              </div>

              {/* Tone Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Tone
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {TONE_OPTIONS.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => setTone(option.id)}
                      className={`p-3 text-left border-2 rounded-xl transition-all ${
                        tone === option.id
                          ? 'border-[#5a20ff] bg-[#5a20ff]/5'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className={`font-medium text-sm ${
                        tone === option.id ? 'text-[#5a20ff]' : 'text-gray-900'
                      }`}>
                        {option.label}
                      </div>
                      <div className="text-xs text-gray-500">{option.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Company Name */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Building2 size={14} />
                  Company Name
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="e.g., MedTech Solutions"
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#5a20ff]"
                />
              </div>

              {/* Signature */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <FileText size={14} />
                  Email Signature
                </label>
                <textarea
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder="Best,&#10;Shelly&#10;Sales Director"
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#5a20ff] resize-none"
                />
              </div>

              {/* Value Propositions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Key Value Propositions
                </label>
                <div className="space-y-2">
                  {valueProps.map((prop, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={prop}
                        onChange={(e) => handleValuePropChange(index, e.target.value)}
                        placeholder="e.g., 50% faster clinical trials"
                        className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#5a20ff]"
                      />
                      {valueProps.length > 1 && (
                        <button
                          onClick={() => removeValueProp(index)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  onClick={addValueProp}
                  className="mt-2 text-sm text-[#5a20ff] hover:underline"
                >
                  + Add another
                </button>
              </div>

              {/* Custom Instructions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Instructions
                </label>
                <textarea
                  value={customInstructions}
                  onChange={(e) => setCustomInstructions(e.target.value)}
                  placeholder="e.g., Always mention FDA approval. Never use the word 'synergy'. Keep messages under 100 words."
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#5a20ff] resize-none"
                />
                <p className="mt-1 text-xs text-gray-400">
                  Specific rules for the AI to follow when generating messages
                </p>
              </div>

              {/* Sample Messages */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sample Messages{' '}
                  <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Paste examples of your actual outreach to train the AI on your style
                </p>
                <div className="space-y-2">
                  {sampleMessages.map((msg, index) => (
                    <div key={index} className="flex gap-2">
                      <textarea
                        value={msg}
                        onChange={(e) => handleSampleChange(index, e.target.value)}
                        placeholder="Paste an example email here..."
                        rows={3}
                        className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#5a20ff] resize-none"
                      />
                      {sampleMessages.length > 1 && (
                        <button
                          onClick={() => removeSampleMessage(index)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors self-start"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button
                  onClick={addSampleMessage}
                  className="mt-2 text-sm text-[#5a20ff] hover:underline"
                >
                  + Add sample
                </button>
              </div>

              {/* Error */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl">
          <div>
            {voiceSource === 'personal' && hasOrgVoice && (
              <button
                onClick={handleSwitchToOrg}
                disabled={isSaving}
                className="text-xs text-[#5a20ff] hover:text-[#4a18d9] font-medium transition-colors disabled:opacity-50"
              >
                Switch to team brand voice
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || isLoading}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-[#5a20ff] rounded-lg hover:bg-[#4a18d9] disabled:opacity-50 transition-colors"
            >
              {isSaving ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Saving...
                </>
              ) : saved ? (
                <>
                  <Check size={16} />
                  Saved
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save as my voice
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}







